import { Button, Image } from "antd";
import BanIcon from "components/Icons/BanIcon";
import CheckIcon from "components/Icons/CheckIcon";
import DeleteIcon from "components/Icons/DeleteIcon";
import EditIcon from "components/Icons/EditIcon";
import { statusBadge } from "components/Table";
import { RouteName } from "constants/route";
import { IDriver } from "models/driver";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "./DeleteModal";
import ActivateModal from "./ActivateModal";
import BanModal from "./BanModal";
import VerifyModal from "./VerifyModal";
import WalletIcon from "components/Icons/WalletIcon";
import FleetIcon from "components/Icons/FleetIcon";
import { isAdmin } from "helpers/privilege";
import QRCode from "react-qr-code";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import header from "assets/images/header.jpg";
import stamp from "assets/images/stamp.png";
import fadedLogo from "assets/images/emun-faded-logo.png";
import TextLogo from "assets/images/emun-text-logo.png";
import moment from "moment";

interface IProps {
  handleOpenBan: () => void;
  handleOpenDelete: () => void;
  handleOpenActivate: () => void;
  handleOpenVerify: () => void;
  handleCloseDelete: () => void;
  handleCloseActivate: () => void;
  handleCloseBan: () => void;
  handleCloseVerify: () => void;
  reload: () => void;
  openDelete: boolean;
  openActivate: boolean;
  openBan: boolean;
  openVerify: boolean;
  data?: IDriver | undefined;
}

export default function DriverInfo({
  data,
  openDelete,
  handleCloseDelete,
  handleOpenBan,
  reload,
  handleOpenDelete,
  handleOpenActivate,
  handleCloseActivate,
  handleCloseBan,
  handleOpenVerify,
  openActivate,
  handleCloseVerify,
  openBan,
  openVerify,
}: IProps) {
  const navigate = useNavigate();
  const handleFleetNavigation = () => {
    if (data?.fleet?.id) {
      navigate(RouteName.FLEET_DETAIL.replace(":id", data.fleet.id));
    }
  };
  const handleWalletNavigation = () => {
    if (data?.id) {
      navigate(RouteName.DRIVER_WALLET_DETAIL.replace(":id", data.id));
    }
  };

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  return (
    <div
      className="w-full bg-white rounded-lg p-6"
      style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
    >
      <div className="w-full">
        <h4 className="text-lg font-medium">Driver Information</h4>
        {data && !data.is_active && data.penalty_reason && (
          <div className="w-full mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
            <p className="font-bold mb-1 underline">Reason for Banning:</p>
            <p className="text-sm italic italic">"{data.penalty_reason}"</p>
          </div>
        )}
        <div className="w-full mt-3">
          <Image
            src={data?.image}
            preview={false}
            height={150}
            className="rounded-md"
          />
        </div>
        <div className="w-full mt-2 flex flex-wrap gap-x-4 justify-between">
          <div className="flex flex-col">
            <table className="text-sm">
              <tbody>
                <tr>
                  <td className="font-bold min-w-36">Full Name</td>
                  <td className="min-w-36">
                    {data?.first_name} {data?.middle_name} {data?.last_name}
                  </td>
                </tr>
                <tr>
                  <td className="font-bold min-w-36">Full Name (Local)</td>
                  <td className="min-w-36">
                    {data?.local_full_name ?? "Not set"}
                  </td>
                </tr>
                <tr className="mt-4">
                  <td className="font-bold min-w-36">Phone Number</td>
                  <td className="min-w-36">{data?.phone}</td>
                </tr>
                <tr className="mt-4">
                  <td className="font-bold min-w-36">Backup Phone</td>
                  <td className="min-w-36">{data?.backup_phone}</td>
                </tr>
                <tr className="mt-4">
                  <td className="font-bold min-w-36">Email</td>
                  <td className="min-w-36">{data?.email ?? "Not set"}</td>
                </tr>
                <tr className="mt-4">
                  <td className="font-bold min-w-36">Registered By</td>
                  <td className="min-w-36">
                    {data?.account
                      ? data?.account?.first_name
                      : "Registered By App"}{" "}
                    {data?.account?.last_name}
                  </td>
                </tr>
                <tr className="mt-4">
                  <td className="font-bold min-w-36">Status</td>
                  <td className="min-w-36">
                    {statusBadge(data?.is_active ?? false)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {data && (
            <div className="relative flex flex-col justify-start items-end gap-y-2">
              <div className="">
                <Button
                  onClick={reactToPrintFn}
                  type="primary"
                  className="bg-yellow-500 px-20 text-base shadow-2xl shadow-yellow-400"
                >
                  Print
                </Button>
              </div>
              <div className="w-full hidden">
                <div
                  className="relative flex flex-col justify-center items-center"
                  ref={contentRef}
                >
                  <div className="p-6 flex flex-col">
                    <div className="w-full max-w-3xl">
                      <Image src={header} className="w-full" />
                      <div className="w-full pl-5 flex gap-x-5 relative">
                        <img
                          src={fadedLogo}
                          alt="logo"
                          className="absolute right-3 top-4 z-0"
                          height={155}
                          width={155}
                        />

                        <Image
                          className="mt-4 object-cover"
                          src={data.image}
                          preview={false}
                          height={250}
                          width={240}
                        />
                        <img
                          src={stamp}
                          alt="logo"
                          className="absolute top-[216px] z-10 left-7"
                          height={200}
                          width={200}
                        />
                        <div className="w-full flex flex-col gap-y-3 mt-1">
                          <div className="w-full">
                            <p className="font-bold">
                              ሽም:{" "}
                              <span className="text-">
                                {data.local_full_name ??
                                  `${data.first_name} ${data.middle_name} ${data.last_name}`}
                              </span>
                            </p>
                            <p className="font-bold">
                              Name:{" "}
                              <span className="text-">
                                {data.first_name} &nbsp; {data.middle_name}{" "}
                                &nbsp; {data.last_name}
                              </span>
                            </p>
                          </div>
                          <div className="w-full">
                            <p className="font-bold">
                              ታርጋ:{" "}
                              <span className="text-">
                                {data.fleet.plate_number}
                              </span>
                            </p>
                            <p className="font-bold">
                              Plate No:{" "}
                              <span className="text-">
                                {data.fleet.plate_number}
                              </span>
                            </p>
                          </div>
                          <div className="w-full">
                            <p className="font-bold">
                              መግንሒ ፍቓድ ቁፅሪ:{" "}
                              <span className="text-">
                                {data.fleet.license_number}
                              </span>
                            </p>
                            <p className="font-bold">
                              Driver License No:{" "}
                              <span className="text-">
                                {data.fleet.license_number}
                              </span>
                            </p>
                          </div>
                          <div className="w-full">
                            <p className="font-bold">
                              ቁፅሪ ስልኪ:{" "}
                              <span className="text-">{data.phone}</span>
                            </p>
                            <p className="font-bold">
                              Phone No:{" "}
                              <span className="text-">{data.phone}</span>
                            </p>
                          </div>
                          <div className="w-full justify-start flex">
                            <Image
                              preview={false}
                              src={TextLogo}
                              height={150}
                              width={150}
                            />
                          </div>
                          <div className="w-full">
                            <p className="font-bold">
                              ፍቓድ ዝተዉሃበሉ ዕለት:{" "}
                              <span className="text-">
                                {moment(new Date()).format("DD MMM, YYYY")}
                              </span>
                            </p>
                            <p className="font-bold">
                              Printed Date:{" "}
                              <span className="text-">
                                {moment(new Date()).format("DD MMM, YYYY")}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-0 right-0 p-7 pr-6">
                        <div className="w-full border-[6px] border-black p-2">
                          <QRCode
                            value={`${process.env.REACT_APP_ADMIN_URL}/drivers/${data.qrcode}/qrcode`}
                            size={260}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="Q" // L, M, Q, H (error correction level)
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative flex flex-col justify-center items-center">
                <QRCode
                  value={`${process.env.REACT_APP_ADMIN_URL}/drivers/${data.qrcode}/qrcode`}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="Q" // L, M, Q, H (error correction level)
                />
              </div>
            </div>
          )}
        </div>
        <div className="w-full flex items-center justify-start gap-4 flex-wrap">
          <Link to={RouteName.EDIT_DRIVER.replace(":id", data!.id)}>
            <Button
              className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 hover:text-black"
              type="primary"
            >
              <span className="text-sm inline-block">
                <EditIcon />
              </span>
              <span>Edit</span>
            </Button>
          </Link>
          {isAdmin() &&
            (data?.is_active ? (
              <Button
                onClick={handleOpenBan}
                className="h-auto w-fit flex items-center px-6 py-2 gap-x-1"
                type="primary"
                danger
              >
                <span className="text-sm inline-block">
                  <BanIcon />
                </span>
                <span>Ban</span>
              </Button>
            ) : (
              <Button
                onClick={handleOpenActivate}
                className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 bg-success shadow-success shadow-sm hover:shadow-black"
                type="primary"
              >
                <span className="text-sm inline-block">
                  <CheckIcon />
                </span>
                <span>Activate</span>
              </Button>
            ))}
          {isAdmin() && data?.is_verified === false && (
            <Button
              onClick={handleOpenVerify}
              className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 bg-success shadow-success shadow-sm hover:shadow-black"
              type="primary"
            >
              <span className="text-sm inline-block">
                <CheckIcon />
              </span>
              <span>Verify</span>
            </Button>
          )}
          {isAdmin() && (
            <Button
              onClick={handleOpenDelete}
              className="h-auto w-fit flex items-center px-6 py-2 gap-x-1"
              type="primary"
              danger
            >
              <span className=" inline-block">
                <DeleteIcon />
              </span>
              <span>Delete</span>
            </Button>
          )}
          <Button
            onClick={handleWalletNavigation}
            className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 bg-success shadow-success shadow-sm hover:shadow-black"
            type="primary"
          >
            <span className=" inline-block">
              <WalletIcon />
            </span>
            <span>Wallet</span>
          </Button>
          <Button
            onClick={handleFleetNavigation}
            className="h-auto w-fit flex items-center px-6 py-2 gap-x-1 bg-black shadow-black"
            type="primary"
          >
            <span className=" inline-block">
              <FleetIcon />
            </span>
            <span>Fleet</span>
          </Button>
        </div>
      </div>
      <DeleteModal
        driver={data!}
        handleClose={handleCloseDelete}
        open={openDelete}
      />
      <ActivateModal
        reload={reload}
        driver={data!}
        handleClose={handleCloseActivate}
        open={openActivate}
      />
      <BanModal
        driver={data!}
        handleClose={handleCloseBan}
        open={openBan}
        reload={reload}
      />
      <VerifyModal
        reload={reload}
        driver={data!}
        handleClose={handleCloseVerify}
        open={openVerify}
      />
    </div>
  );
}
